/* =============================================================================
   DAILE STUDIO (Mono, rounds 9–10c) — the photo → artwork tool. Markup: sections/mn-studio-app.liquid
   (config JSON in [data-st-config]); styles: assets/mn-studio.css.

   1. Category radiogroup (People / Pets / Families / Mixed): tip text, camera facing, extra photos.
   2. Photos: file chooser, camera (touch devices), drag & drop on the stage; decoded in the browser
      (HEIC where the browser can), resized to ≤ 1536 px (JPEG 0.9). Families / Mixed: up to 3 photos.
   3. Styles radiogroup (blocks).
   4. One guided button (sticky bar on phones / under the stage on desktop):
      no photo → "Add your photo" (opens the chooser) · no style → "Choose a style" · else "Create my artwork".
   5. E-mail gate before the first generation (localStorage 'mn-studio' { email, consent, used, remaining, cat }).
      LIVE + consent → window.mnOffer.subscribe(email, { source }) (Klaviyo list or Shopify customer form).
      MOCK → nothing leaves the browser.
   6. Generation:
      MOCK  — the photo is painted with a canvas filter per style (below, "Mock painters") after a fake delay
              (setting), at the model's output size (1024×1536 / 1536×1024 / 1024×1024).
      LIVE  — POST <endpoint>/generate (multipart: image[, image, image], style, category, email, consent,
              locale, client, turnstile?) → { ok, id, result_url, print_url?, width, height, remaining, limit }
              or { ok: false, error: 'moderation' | 'limit_email' | 'limit_ip' | 'busy' | 'bad_image' | … }.
              See cloud/daile-studio/README.md.
   7. Result: before / after (range input, keyboard + touch), gallery of this visit's artworks, watermarked
      preview download (canvas, "DAILE STUDIO · PREVIEW"), "N of 3 free previews left".
   8. "Make it a puzzle": print_url (live, when the Worker has an upscaler) or the artwork, below 3000 px smoothed
      to 2× (so the customizer's pixel-based quality check does not warn; the real print master comes from the
      upscale step, see the README), goes to IndexedDB 'daile-cz' / 'photos' / 'photo_pdp-pending' — the same
      hand-off the custom-puzzle PDP uses (assets/mn-pdp.js §7) — then /pages/customizer?pdp=1[&set=…]:
      the customizer (assets/dv-customizer.js bootPdpPending, read-only) accepts it as an upload and shows
      "Your photo is ready — now choose your set". With the Mono pop-up on, window.mnCzpop.open() shows it over
      this page. sessionStorage 'daile-studio-handoff' = { id, style, mode, result_url, print_url, w, h, at } for a
      future customizer / production hook. Events on document: 'mn-studio:puzzle' { style, id, mode, result_url,
      print_url }, 'mn-studio:ready' { style, id, mode }, 'mn-studio:email' { consent, mode }.

   1b. Category examples (round 10c, snippets/mn-studio-examples): the strip follows the category (fade); the empty
      stage shows the category's first example big; a tap on an example shows it on the empty stage, or — with a
      photo on the stage — in a lightbox. No style is pre-selected (DAILE's artworks are not one of the 8 styles).
   6b. Robustness (round 10c): while painting, leaving / reloading the page asks first (beforeunload); the running
      job is marked in sessionStorage 'mn-studio-pending' { at, style, cat, mode } and cleared when it ends — after
      a reload / closed tab the flag is still there and the tool shows a calm "your last preview was interrupted"
      notice (shown before paint by an inline script in the section, so no layout shift) and pre-selects the style.
      Note: cloud/daile-studio/worker.js reserves the free preview before painting and refunds it only when painting
      fails; a request cut off by a reload is not refunded by the Worker, so the notice makes no promise about it.
      Progress copy "Usually 20–40 seconds", elapsed time, and a "taking longer" line after 50 s (live).

   0. Coming soon (round 10): mock mode + "Demo mode visible to visitors" off → on the published theme the tool is
      replaced by an e-mail sign-up (window.mnOffer.subscribe(email, { source: 'studio' }), else the native
      Shopify customer form) + the styles as a static gallery. Event 'mn-studio:waitlist'.

   Owner test helpers: ?studio_reset=1 forgets the e-mail and the used previews in this browser;
   ?studio_soon=1 shows the coming-soon state (previews / theme dev).
   The DAILE Friday pop-up does not auto-open on this page (sessionStorage 'mn-offer-shown'): the studio has its
   own e-mail gate.
   ============================================================================= */
(() => {
  'use strict';

  const d = document;
  const w = window;
  const html = d.documentElement;
  const root = d.querySelector('[data-mn-studio]');
  if (!root || root.mnStudio) return;
  root.mnStudio = true;

  /* Config + texts ------------------------------------------------------------ */
  let cfg = {};
  try { cfg = JSON.parse(root.querySelector('[data-st-config]').textContent) || {}; } catch (e) { cfg = {}; }
  const T = cfg.t || {};
  const EN = {
    left: '{n} of {total} free previews left',
    create: 'Create my artwork',
    before: 'Before',
    after: 'After',
    puzzle: 'Make it a puzzle',
    photo_h: 'Add your photo',
    style_h: 'Choose a style',
    none_left: 'You have used all {total} free previews. Choose your favourite and make it a puzzle.',
    none_btn: 'No free previews left',
    need_photo: 'Add a photo first.',
    need_style: 'Choose a style first.',
    gen_s1: 'Studying your photo',
    gen_s2: 'Mixing the palette',
    gen_s3: 'Laying the first brushstrokes',
    gen_s4: 'Adding the final details',
    err_gen: 'Something went wrong while painting. Please try again — it did not use a free preview.',
    err_policy: 'We can’t paint this photo. Please choose another one.',
    err_limit: 'Too many previews from this connection today. Please come back tomorrow.',
    err_busy: 'The studio is very busy right now. Please try again in a minute.',
    err_heic: 'This HEIC photo can’t be opened in this browser. Please choose a JPG or PNG — or take a screenshot of the photo and upload that.',
    err_type: 'Please choose a photo (JPG, PNG or HEIC).',
    err_read: 'We could not read this file. Please try another photo.',
    err_small: 'This photo is quite small — the artwork may look soft. A larger photo works best.',
    err_net: 'No connection to the studio. Please check your internet and try again.',
    opening: 'Opening the puzzle designer…',
    result_alt: '{style} artwork made from your photo',
    photo_alt: 'Your photo',
    remove: 'Remove photo',
    gate_bad: 'Please enter a valid e-mail address.',
    announce_photo: 'Photo added.',
    announce_ready: 'Your artwork is ready.',
    view: 'Show {style}',
    ex_tag: 'Example',
    ex_in: 'In the example',
    gen_slow: 'Taking a little longer than usual — still painting.',
    err_style: 'This style opens very soon — please pick another one for now. No free preview was used.',
    saved_note: 'Your artworks are saved in this browser for 30 days.',
    hd_added: 'Added to your cart.',
    wait_ok: 'Thank you — we will e-mail you as soon as it is ready.',
    wait_bad: 'Please enter a valid e-mail address.',
    busy_more: 'One moment…',
    interrupted: 'Your last preview was interrupted because the page was reloaded or closed. Please add your photo again and keep this page open until your artwork appears — usually 20–40 seconds.',
  };
  const t = (k, vars) => {
    let s = (T[k] != null && T[k] !== '') ? String(T[k]) : (EN[k] || '');
    if (vars) Object.keys(vars).forEach((v) => { s = s.split(`{${v}}`).join(String(vars[v])); });
    return s;
  };

  const LIVE = cfg.mode === 'live' && !!cfg.endpoint;
  const ENDPOINT = String(cfg.endpoint || '').replace(/\/+$/, '');
  const LIMIT = Math.max(1, parseInt(cfg.limit, 10) || 3);
  const MAX_SIDE = parseInt(cfg.maxSide, 10) || 1536;
  const MAX_PHOTOS = cfg.multi ? Math.max(1, parseInt(cfg.maxPhotos, 10) || 3) : 1;
  const MULTI_CATS = ['families', 'mixed'];
  // owner 22.10: «People + pet» = two photos — the people first, then the pet (the Worker paints them together)
  const capFor = (cat) => (cat === 'mixed' ? Math.min(2, MAX_PHOTOS) : MAX_PHOTOS);
  const CATS = ['people', 'pets', 'families', 'mixed'];
  const WHOS = ['me', 'couple', 'family', 'pet', 'mixed'];
  const KEY = 'mn-studio';
  const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/;
  const reduce = () => !!(w.matchMedia && w.matchMedia('(prefers-reduced-motion: reduce)').matches);
  const coarse = () => !!(w.matchMedia && w.matchMedia('(pointer: coarse)').matches);
  const isRtl = () => (html.getAttribute('dir') || '').toLowerCase() === 'rtl';
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  const frame = () => new Promise((r) => requestAnimationFrame(() => r()));
  const shopRoot = (() => {
    let r = (w.Shopify && w.Shopify.routes && w.Shopify.routes.root) || cfg.root || '/';
    if (r.charAt(r.length - 1) !== '/') r += '/';
    return r;
  })();
  const emit = (name, detail) => { try { d.dispatchEvent(new CustomEvent(name, { detail })); } catch (e) { /* old browsers */ } };

  /* Storage -------------------------------------------------------------------- */
  const store = {
    get() { try { return JSON.parse(w.localStorage.getItem(KEY) || '{}') || {}; } catch (e) { return {}; } },
    set(patch) { try { w.localStorage.setItem(KEY, JSON.stringify(Object.assign(store.get(), patch))); } catch (e) { /* private mode */ } },
    clear() { try { w.localStorage.removeItem(KEY); } catch (e) { /* */ } },
  };
  if (/[?&]studio_reset=1(?:&|$)/.test(w.location.search)) store.clear();
  // The studio has its own e-mail gate: keep the DAILE Friday pop-up from auto-opening over the tool.
  try { if (!cfg.design) w.sessionStorage.setItem('mn-offer-shown', '1'); } catch (e) { /* */ }

  /* 0. Coming soon (round 10) ------------------------------------------------------------------- */
  // Mock mode + "Demo mode visible to visitors" off: visitors of the PUBLISHED theme get an e-mail sign-up
  // ("3 free previews at launch") instead of a simulated demo. Liquid decides with theme.role (cfg.soon, the
  // tool is then not rendered at all); the browser repeats the check only when Liquid could not tell (cfg.role
  // empty). ?studio_soon=1 forces the soon state for a look (previews / theme dev).
  const soonEl = root.querySelector('[data-st-soon]');
  const soonNow = (() => {
    if (!soonEl || !cfg.soonPossible) return false;
    if (cfg.soon) return true;
    if (/[?&]studio_soon=1(?:&|$)/.test(w.location.search)) return true;
    if (cfg.role) return false;
    const sh = w.Shopify || {};
    const role = sh.theme && sh.theme.role;
    const host = String(w.location.hostname || '');
    const local = /^(127\.0\.0\.1|localhost|\[::1\])$/.test(host) || /\.(localhost|test)$/.test(host);
    const preview = /[?&]preview_theme_id=/.test(w.location.search) || !!sh.designMode;
    return role === 'main' && !local && !preview && !cfg.design;
  })();

  function initSoon() {
    root.setAttribute('data-st-soon-on', '');
    soonEl.hidden = false;
    const form = soonEl.querySelector('form');
    const input = soonEl.querySelector('[data-st-soon-email]');
    const go = soonEl.querySelector('[data-st-soon-go]');
    const label = soonEl.querySelector('[data-st-soon-label]');
    const err = soonEl.querySelector('[data-st-soon-err]');
    const done = soonEl.querySelector('[data-st-soon-done]');
    const panel = soonEl.querySelector('[data-st-soon-panel]') || soonEl;
    const say = (text) => {
      if (err) { err.textContent = text || ''; err.hidden = !text; }
      if (input) { if (text) input.setAttribute('aria-invalid', 'true'); else input.removeAttribute('aria-invalid'); }
    };
    const showDone = (focus) => {
      if (!form || !done) return;
      form.classList.add('is-done');
      done.hidden = false;
      if (focus) { try { done.focus({ preventScroll: true }); } catch (e) { /* */ } }
    };
    let busy = false;
    const setBusy = (on) => {
      busy = on;
      if (!go) return;
      go.setAttribute('aria-busy', on ? 'true' : 'false');
      if (label) {
        if (on) { label.setAttribute('data-label', label.textContent); label.textContent = go.getAttribute('data-sending') || label.textContent; }
        else if (label.hasAttribute('data-label')) label.textContent = label.getAttribute('data-label');
      }
    };
    if (store.get().soon) showDone(false);

    // Hero / FAQ "Notify me at launch" → the sign-up panel, cursor in the e-mail field
    d.addEventListener('click', (e) => {
      const a = e.target && e.target.closest ? e.target.closest('[data-st-start]') : null;
      if (!a) return;
      e.preventDefault();
      try { panel.scrollIntoView({ behavior: reduce() ? 'auto' : 'smooth', block: 'center' }); } catch (er) { panel.scrollIntoView(); }
      if (input && !(form && form.classList.contains('is-done'))) {
        setTimeout(() => { try { input.focus({ preventScroll: true }); } catch (er) { /* */ } }, reduce() ? 0 : 450);
      }
    });
    if (input) input.addEventListener('input', () => { if (err && !err.hidden) say(''); });
    if (form) form.addEventListener('submit', (e) => {
      const offer = w.mnOffer;
      // without the DAILE Friday pop-up section: the native Shopify customer form (tags newsletter,daile-studio)
      if (!offer || typeof offer.subscribe !== 'function') return;
      e.preventDefault();
      e.stopPropagation();
      if (busy) return;
      const email = String((input && input.value) || '').trim();
      if (!EMAIL_RE.test(email)) { say(t('gate_bad')); if (input) input.focus(); return; }
      say('');
      setBusy(true);
      Promise.resolve()
        .then(() => offer.subscribe(email, { source: cfg.source || 'studio' }))
        .then((res) => {
          if (res && res.ok === false) throw res;
          store.set({ soon: 1, soonAt: new Date().toISOString() });
          emit('mn-studio:waitlist', { source: cfg.source || 'studio' });
          setBusy(false);
          showDone(true);
        })
        .catch((er) => {
          setBusy(false);
          say((er && er.message) || t('err_net'));
        });
    });
    w.mnStudio = Object.freeze({ mode: 'soon', reset: () => store.clear() });
  }

  if (soonNow) { initSoon(); return; }

  /* Elements ------------------------------------------------------------------- */
  const $ = (sel, el) => (el || root).querySelector(sel);
  const $$ = (sel, el) => Array.from((el || root).querySelectorAll(sel));
  const els = {
    cats: $$('[data-st-cat]'),
    stage: $('[data-st-stage]'),
    tip: $('[data-st-tip]'),
    file: $('[data-st-file]'),
    camera: $('[data-st-camera]'),
    cameraWrap: $('[data-st-camera-wrap]'),
    photo: $('[data-st-photo]'),
    photoImg: $('[data-st-photo-img]'),
    busy: $('[data-st-busy]'),
    status: $('[data-st-status]'),
    bar: $('[data-st-bar]'),
    compare: $('[data-st-compare]'),
    before: $('[data-st-before]'),
    after: $('[data-st-after]'),
    range: $('[data-st-range]'),
    photos: $('[data-st-photos]'),
    thumbs: $('[data-st-thumbs]'),
    add: $('[data-st-add]'),
    addWrap: $('[data-st-add-wrap]'),
    change: $('[data-st-change]'),
    msg: $('[data-st-msg]'),
    result: $('[data-st-result]'),
    resultStyle: $('[data-st-result-style]'),
    puzzle: $('[data-st-puzzle]'),
    puzzleLabel: $('[data-st-puzzle-label]'),
    download: $('[data-st-download]'),
    again: $('[data-st-again]'),
    reo: $('[data-st-reo]'),
    gallery: $('[data-st-gallery]'),
    galleryList: $('[data-st-gallery-list]'),
    creates: $$('[data-st-create]'),
    lefts: $$('[data-st-left]'),
    styles: $$('[data-st-style]'),
    stylesWrap: $('[data-st-styles]'),
    gate: $('[data-st-gate]'),
    gateForm: $('[data-st-gate-form]'),
    email: $('[data-st-email]'),
    consent: $('[data-st-consent]'),
    gateErr: $('[data-st-gate-err]'),
    gateClose: $('[data-st-gate-close]'),
    exListsWrap: $('[data-st-ex-lists]'),
    exLists: $$('[data-st-ex-cat]'),
    exCards: $$('[data-st-ex-card]'),
    exStageImg: $('[data-st-exstage-img]'),
    exStageBf: $('[data-st-exbf]'),
    exStageBfImg: $('[data-st-exbf-img]'),
    exStageBf2: $('[data-st-exbf2]'),
    exStageBf2Img: $('[data-st-exbf2-img]'),
    exStageName: $('[data-st-exstage-name]'),
    exSee: $('[data-st-ex-see]'),
    exbox: $('[data-st-exbox]'),
    exboxImg: $('[data-st-exbox-img]'),
    exboxTitle: $('[data-st-exbox-title]'),
    exboxClose: $('[data-st-exbox-close]'),
    notice: $('[data-st-notice]'),
    noticeText: $('[data-st-notice-text]'),
    noticeX: $('[data-st-notice-x]'),
    time: $('[data-st-time]'),
  };
  if (!els.stage) return;

  const live = d.createElement('p');
  live.className = 'mn-sr-only';
  live.setAttribute('aria-live', 'polite');
  root.appendChild(live);
  const announce = (text) => { live.textContent = ''; setTimeout(() => { live.textContent = text; }, 60); };

  /* Pending job flag (round 10c): survives a reload of this tab, not a new tab. */
  const PENDING = 'mn-studio-pending';
  const pending = {
    get() { try { return JSON.parse(w.sessionStorage.getItem(PENDING) || 'null'); } catch (e) { return null; } },
    set(v) { try { w.sessionStorage.setItem(PENDING, JSON.stringify(v)); } catch (e) { /* private mode */ } },
    clear() { try { w.sessionStorage.removeItem(PENDING); } catch (e) { /* */ } },
  };
  let started = false;

  /* State ------------------------------------------------------------------------ */
  const saved = store.get();
  const S = {
    cat: CATS.indexOf(saved.cat) > -1 ? saved.cat : (CATS.indexOf(cfg.defaultCat) > -1 ? cfg.defaultCat : 'people'),
    who: WHOS.indexOf(saved.who) > -1 ? saved.who : 'me', // R11b: the answer to «Who's in your photo?»
    photos: [],       // { blob, url, w, h, canvas }
    style: '',
    styleName: '',
    worker: '',       // R11: prompt id sent to the generator (a new style may reuse a close one until its own prompt is live)
    ready: true,      // R11: false = the style is shown but its prompt is not connected yet
    results: [],      // { id, style, name, blob, url, w, h, beforeUrl, printBlob, printUrl }
    current: -1,
    busy: false,
    orient: '',       // 23.10: '' = the photo's orientation; 'h' | 'v' = «other orientation» re-paint
    view: 'empty',    // stage: empty | photo | busy | result
  };
  const remaining = () => {
    const st = store.get();
    if (LIVE && Number.isFinite(st.remaining)) return Math.max(0, st.remaining);
    return Math.max(0, LIMIT - (parseInt(st.used, 10) || 0));
  };

  /* Messages ----------------------------------------------------------------------- */
  const showMsg = (text, warn) => {
    if (!els.msg) return;
    els.msg.textContent = text || '';
    els.msg.hidden = !text;
    els.msg.classList.toggle('is-warn', !!warn);
  };

  /* 1. Category ---------------------------------------------------------------------- */
  /* R11b: ONE question «Who's in your photo?» — the button carries the Worker category (data-st-cat: likeness rules)
     and the answer (data-st-who: me | couple | family | pet | mixed) that filters the styles and swaps their pictures. */
  function setCat(arg, focus) {
    const btn = (arg && arg.nodeType === 1) ? arg
      : els.cats.find((b) => b.getAttribute('data-st-who') === arg) || els.cats.find((b) => b.getAttribute('data-st-cat') === arg) || els.cats[0];
    if (!btn) return;
    const cat = btn.getAttribute('data-st-cat');
    const who = btn.getAttribute('data-st-who') || 'me';
    if (CATS.indexOf(cat) < 0) return;
    S.cat = cat;
    S.who = who;
    store.set({ cat, who });
    let tip = '';
    els.cats.forEach((b) => {
      const on = b === btn;
      b.setAttribute('aria-checked', on ? 'true' : 'false');
      b.tabIndex = on ? 0 : -1;
      if (on) { tip = b.getAttribute('data-tip') || ''; if (focus) b.focus(); }
    });
    if (els.tip) els.tip.textContent = tip;
    showExamples(who, started);
    applyWho(who);
    syncExStyle();
    if (els.camera) els.camera.setAttribute('capture', cat === 'people' ? 'user' : 'environment');
    const keep = MULTI_CATS.indexOf(cat) < 0 ? 1 : capFor(cat);
    if (S.photos.length > keep) {
      S.photos.slice(keep).forEach(dropPhoto);
      S.photos = S.photos.slice(0, keep);
    }
    renderPhotos();
  }

  /* Styles for the answer: only the ones that suit it, connected ones first, each card showing ITS picture for that
     answer (Birmingham: the man alone / the couple / the family) and the photo it was painted from. */
  function applyWho(who) {
    els.styles.forEach((b) => {
      const best = `,${b.getAttribute('data-st-best') || ''},`;
      const fits = best === ',,' || best.indexOf(`,${who},`) > -1;
      b.hidden = !fits && b.getAttribute('aria-checked') !== 'true';
      b.style.order = (b.getAttribute('data-st-ready') === 'false' ? 1 : 0) + (fits ? 0 : 2);
      const pic = b.getAttribute(`data-st-img-${who}`);
      if (!pic) return;
      const [s640, s1200, bf, bf2] = pic.split('|');
      const img = b.querySelector('.mn-st-card__img');
      if (img && img.getAttribute('src') !== s640) { img.src = s640; img.srcset = `${s640} 640w, ${s1200} 1200w`; }
      const box = b.querySelector('[data-st-card-bf]');
      if (box) {
        box.hidden = !bf;
        const bi = box.querySelector('img');
        if (bi && bf) bi.src = bf;
      }
      const box2 = b.querySelector('[data-st-card-bf2]'); // People + pet: the pet's own photo
      if (box2) {
        box2.hidden = !bf2;
        const bi2 = box2.querySelector('img');
        if (bi2 && bf2) bi2.src = bf2;
      }
    });
    if (els.stylesWrap) els.stylesWrap.setAttribute('data-who', who);
  }

  /* Radiogroup keyboard (categories + styles): arrows move and select, Home / End. */
  function radioKeys(items, select) {
    items.forEach((b, i) => {
      b.addEventListener('keydown', (e) => {
        const horizontal = !(b.closest('.mn-st-seg') && w.matchMedia && w.matchMedia('(min-width: 1024px)').matches);
        let step = 0;
        if (e.key === 'ArrowDown') step = 1;
        else if (e.key === 'ArrowUp') step = -1;
        else if (e.key === 'ArrowRight') step = horizontal && isRtl() ? -1 : 1;
        else if (e.key === 'ArrowLeft') step = horizontal && isRtl() ? 1 : -1;
        else if (e.key === 'Home') step = -i;
        else if (e.key === 'End') step = items.length - 1 - i;
        else if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); select(b, true); return; }
        else return;
        e.preventDefault();
        const next = items[(i + step + items.length) % items.length];
        select(next, true);
      });
      b.addEventListener('click', () => select(b, false));
    });
  }
  radioKeys(els.cats, (b, focus) => { if (!S.busy) setCat(b, focus); });

  /* 1b. Category examples ------------------------------------------------------------- */
  let exTimer = 0;
  let exImgTimer = 0;
  const exList = (cat) => els.exLists.find((l) => l.getAttribute('data-st-ex-cat') === cat) || null;
  function markCurrent(card) {
    const list = card && card.closest('[data-st-ex-cat]');
    if (!list) return;
    list.querySelectorAll('[data-st-ex-card]').forEach((c) => c.setAttribute('aria-current', c === card ? 'true' : 'false'));
  }
  function setStageExample(card, fade) {
    const img = els.exStageImg;
    if (!img || !card) return;
    const src = card.getAttribute('data-src') || '';
    const alt = `${t('ex_tag')}: ${card.getAttribute('data-title') || ''}`;
    clearTimeout(exImgTimer);
    const bf = card.getAttribute('data-bf') || '';
    S.exCard = card;
    if (els.exStageName) els.exStageName.textContent = card.getAttribute('data-title') || '';
    markExampleStyle(card);
    if (img.getAttribute('src') === src) { img.alt = alt; img.style.opacity = ''; return; }
    const swap = () => {
      img.src = src;
      img.alt = alt;
      // owner 22.10 r2: the example always shows the photo it was painted from
      if (els.exStageBf) { els.exStageBf.hidden = !bf; if (bf && els.exStageBfImg) els.exStageBfImg.src = bf; }
      const bf2 = card.getAttribute('data-bf2') || '';
      if (els.exStageBf2) { els.exStageBf2.hidden = !bf2; if (bf2 && els.exStageBf2Img) els.exStageBf2Img.src = bf2; }
    };
    if (!fade || reduce() || S.view !== 'empty') { swap(); img.style.opacity = ''; return; }
    img.style.opacity = '0';
    exImgTimer = setTimeout(() => {
      swap();
      let shown = false;
      const show = () => { if (shown) return; shown = true; requestAnimationFrame(() => { img.style.opacity = ''; }); };
      if (img.complete && img.naturalWidth) show();
      else {
        img.addEventListener('load', show, { once: true });
        img.addEventListener('error', show, { once: true });
        setTimeout(show, 700);
      }
    }, 170);
  }
  /* Owner 22.10 night: the example on the stage names its style, and the style is chosen below for the visitor who
     has not picked one yet (open styles only) — «Choose a photo» then paints in the style of the example they see. */
  /* Owner 22.10 night: make it obvious which style the example is — the style card below gets a gold ring and an
     «In the example» badge; «See this style ↓» on the stage scrolls to it. */
  function markExampleStyle(card) {
    const id = exStyleOf(card);
    els.styles.forEach((b) => {
      const on = !!id && b.getAttribute('data-st-style') === id;
      b.classList.toggle('is-example', on);
      if (on) b.setAttribute('data-ex-label', t('ex_in'));
    });
  }
  if (els.exSee) {
    els.exSee.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const b = els.styles.find((x) => x.classList.contains('is-example'));
      if (!b) return;
      if (b.hidden) b.hidden = false;
      scrollToEl(b, 'center');
      b.classList.remove('is-flash');
      void b.offsetWidth;
      b.classList.add('is-flash');
      setTimeout(() => b.classList.remove('is-flash'), 1800);
    });
  }
  function exStyleOf(card) { return ((card && card.getAttribute('data-key')) || '').replace(/-(me|couple|family|pet|mixed)$/, ''); }
  function syncExStyle() {
    if (S.userStyle || S.busy || S.view !== 'empty' || !S.exCard) return;
    const id = exStyleOf(S.exCard);
    const b = els.styles.find((x) => x.getAttribute('data-st-style') === id);
    if (b && !b.hidden && b.getAttribute('data-st-ready') !== 'false') { setStyle(b, false); return; }
    if (S.style) { // the example's style is not open yet: no style stays chosen for it
      S.style = ''; S.styleName = ''; S.worker = ''; S.ready = true;
      els.styles.forEach((x, i) => { x.setAttribute('aria-checked', 'false'); x.tabIndex = i === 0 ? 0 : -1; });
      updateCta();
    }
  }

  function showExamples(cat, animate) {
    const list = exList(cat);
    if (!list) return;
    const first = list.querySelector('[data-st-ex-card]');
    setStageExample(first, animate);
    const apply = () => {
      els.exLists.forEach((l) => { l.hidden = l !== list; });
      markCurrent(first);
    };
    clearTimeout(exTimer);
    if (!animate || reduce() || !els.exListsWrap || !list.hidden) {
      if (els.exListsWrap) els.exListsWrap.classList.remove('is-fading');
      apply();
      return;
    }
    els.exListsWrap.classList.add('is-fading');
    exTimer = setTimeout(() => {
      apply();
      requestAnimationFrame(() => requestAnimationFrame(() => els.exListsWrap.classList.remove('is-fading')));
    }, 160);
  }

  // Example lightbox (a photo is already on the stage)
  let exboxOpener = null;
  function openExbox(card) {
    if (!els.exbox || !els.exboxImg) return;
    exboxOpener = card;
    els.exboxImg.src = card.getAttribute('data-src') || '';
    els.exboxImg.alt = `${t('ex_tag')}: ${card.getAttribute('data-title') || ''}`;
    if (els.exboxTitle) els.exboxTitle.textContent = card.getAttribute('data-title') || '';
    const bf = card.getAttribute('data-bf') || '';
    const bfw = $('[data-st-exbox-bf]');
    const bfi = $('[data-st-exbox-bf-img]');
    if (bfw) { bfw.hidden = !bf; if (bf && bfi) bfi.src = bf; }
    const bf2 = card.getAttribute('data-bf2') || '';
    const bfw2 = $('[data-st-exbox-bf2]');
    const bfi2 = $('[data-st-exbox-bf2-img]');
    if (bfw2) { bfw2.hidden = !bf2; if (bf2 && bfi2) bfi2.src = bf2; }
    try { els.exbox.showModal(); } catch (e) { els.exbox.setAttribute('open', ''); }
  }
  function closeExbox() {
    if (!els.exbox) return;
    try { els.exbox.close(); } catch (e) { els.exbox.removeAttribute('open'); }
    if (exboxOpener) { try { exboxOpener.focus({ preventScroll: true }); } catch (e) { /* */ } }
  }
  if (els.exboxClose) els.exboxClose.addEventListener('click', closeExbox);
  if (els.exbox) els.exbox.addEventListener('click', (e) => { if (e.target === els.exbox) closeExbox(); }); // backdrop

  // Warm-up: once the tool is near the screen, the other categories' small cards load in the background (≈ 17 KB
  // each); pointing at / touching a category preloads its first big example, so the switch fades in at once.
  if (els.exListsWrap && 'IntersectionObserver' in w) {
    const io = new IntersectionObserver((entries) => {
      if (!entries.some((e) => e.isIntersecting)) return;
      io.disconnect();
      els.exLists.forEach((l) => l.querySelectorAll('img[loading="lazy"]').forEach((im) => { im.loading = 'eager'; }));
    }, { rootMargin: '300px 0px' });
    io.observe(els.exListsWrap);
  }
  const warmed = {};
  els.cats.forEach((b) => {
    const warm = () => {
      const c = b.getAttribute('data-st-cat');
      const list = exList(c);
      const first = list && list.querySelector('[data-st-ex-card]');
      if (!first || warmed[c]) return;
      warmed[c] = true;
      const im = new Image();
      im.decoding = 'async';
      im.src = first.getAttribute('data-src') || '';
    };
    b.addEventListener('pointerenter', warm);
    b.addEventListener('touchstart', warm, { passive: true });
    b.addEventListener('focus', warm);
  });

  /* Swipe the examples with a finger (owner 22.10): on the big example of the empty stage and in the lightbox —
     left = next, right = previous, within the current answer's list (wraps around). */
  function stepExample(dir, inBox) {
    const list = els.exLists.find((l) => !l.hidden);
    if (!list) return;
    const cards = Array.from(list.querySelectorAll('[data-st-ex-card]'));
    if (cards.length < 2) return;
    const curEl = inBox ? exboxOpener : cards.find((c) => c.getAttribute('aria-current') === 'true');
    const i = Math.max(0, cards.indexOf(curEl));
    const next = cards[(i + dir + cards.length) % cards.length];
    markCurrent(next);
    if (inBox) openExbox(next); else { setStageExample(next, true); syncExStyle(); }
  }
  function onSwipe(el, ok, cb) {
    if (!el) return;
    let x0 = 0, y0 = 0, t0 = 0, live = false;
    el.addEventListener('touchstart', (e) => {
      live = e.touches.length === 1 && ok();
      if (!live) return;
      x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; t0 = Date.now();
    }, { passive: true });
    el.addEventListener('touchend', (e) => {
      if (!live) return;
      live = false;
      const t = e.changedTouches[0];
      const dx = t.clientX - x0, dy = t.clientY - y0;
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.3 || Date.now() - t0 > 900) return;
      cb(dx < 0 ? 1 : -1);
    }, { passive: true });
  }
  onSwipe(els.stage, () => S.view === 'empty', (dir) => stepExample(dir, false));
  onSwipe(els.exbox, () => !!(els.exbox && (els.exbox.open || els.exbox.hasAttribute('open'))), (dir) => stepExample(dir, true));

  els.exCards.forEach((card) => card.addEventListener('click', () => {
    if (S.view !== 'empty') { openExbox(card); return; }
    markCurrent(card);
    setStageExample(card, true);
    syncExStyle();
    // phones: bring the stage into view when it is mostly below the fold
    const r = els.stage.getBoundingClientRect();
    if (r.top > w.innerHeight * 0.6) scrollToEl(els.stage, 'center');
  }));

  /* 3. Styles ------------------------------------------------------------------------- */
  function setStyle(btn, focus) {
    if (S.busy || !btn) return;
    S.style = btn.getAttribute('data-st-style') || '';
    S.styleName = btn.getAttribute('data-st-name') || S.style;
    S.worker = btn.getAttribute('data-st-worker') || S.style;
    S.ready = btn.getAttribute('data-st-ready') !== 'false';
    els.styles.forEach((b) => {
      const on = b === btn;
      b.setAttribute('aria-checked', on ? 'true' : 'false');
      b.tabIndex = on ? 0 : -1;
    });
    if (focus) btn.focus();
    if (remaining() <= 0 && S.results.length) showMsg(t('none_left', { total: LIMIT }), true);
    else showMsg('');
    updateCta();
  }
  radioKeys(els.styles, (b, focus) => { S.userStyle = true; setStyle(b, focus); });

  /* 2. Photos --------------------------------------------------------------------------- */
  const isHeic = (f) => /hei[cf]/i.test(f.type || '') || /\.(heic|heif)$/i.test(f.name || '');
  const isImage = (f) => /^image\//i.test(f.type || '') || isHeic(f);

  function decode(file) {
    // createImageBitmap honours EXIF orientation by default; <img> is the fallback (and decodes HEIC in Safari).
    const viaImg = () => new Promise((resolve, reject) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => { resolve({ src: img, w: img.naturalWidth, h: img.naturalHeight, done: () => URL.revokeObjectURL(url) }); };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode')); };
      img.src = url;
    });
    if (w.createImageBitmap) {
      return w.createImageBitmap(file, { imageOrientation: 'from-image' })
        .then((bmp) => ({ src: bmp, w: bmp.width, h: bmp.height, done: () => { try { bmp.close(); } catch (e) { /* */ } } }))
        .catch(viaImg);
    }
    return viaImg();
  }

  function canvasOf(wd, ht) {
    const c = d.createElement('canvas');
    c.width = Math.max(1, Math.round(wd));
    c.height = Math.max(1, Math.round(ht));
    return c;
  }
  // Good-quality downscale: halve until close, then the final step.
  function resizeTo(src, sw, sh, tw, th) {
    let cur = src;
    let cw = sw;
    let ch = sh;
    while (cw / 2 >= tw && ch / 2 >= th) {
      const c = canvasOf(cw / 2, ch / 2);
      const x = c.getContext('2d');
      x.imageSmoothingQuality = 'high';
      x.drawImage(cur, 0, 0, c.width, c.height);
      cur = c; cw = c.width; ch = c.height;
    }
    const out = canvasOf(tw, th);
    const ox = out.getContext('2d');
    ox.imageSmoothingQuality = 'high';
    ox.drawImage(cur, 0, 0, out.width, out.height);
    return out;
  }
  // Upscale in two smooth steps (image smoothing 'high'); used only for the customizer hand-off.
  function resizeUp(src, sw, sh, tw, th) {
    const mid = canvasOf(Math.round((sw + tw) / 2), Math.round((sh + th) / 2));
    const mx = mid.getContext('2d');
    mx.imageSmoothingQuality = 'high';
    mx.drawImage(src, 0, 0, mid.width, mid.height);
    const out = canvasOf(tw, th);
    const ox = out.getContext('2d');
    ox.imageSmoothingQuality = 'high';
    ox.drawImage(mid, 0, 0, tw, th);
    return out;
  }
  const toBlob = (canvas, type, q) => new Promise((resolve, reject) => {
    try { canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob'))), type || 'image/jpeg', q || 0.9); } catch (e) { reject(e); }
  });

  async function readPhoto(file) {
    if (!isImage(file)) throw Object.assign(new Error('type'), { code: 'err_type' });
    let dec;
    try { dec = await decode(file); } catch (e) { throw Object.assign(new Error('decode'), { code: isHeic(file) ? 'err_heic' : 'err_read' }); }
    const scale = Math.min(1, MAX_SIDE / Math.max(dec.w, dec.h));
    const tw = Math.round(dec.w * scale);
    const th = Math.round(dec.h * scale);
    const canvas = resizeTo(dec.src, dec.w, dec.h, tw, th);
    dec.done();
    const blob = await toBlob(canvas, 'image/jpeg', 0.9);
    return { blob, url: URL.createObjectURL(blob), w: tw, h: th, canvas, small: Math.min(dec.w, dec.h) < 512 };
  }
  function dropPhoto(p) { if (p && p.url) { try { URL.revokeObjectURL(p.url); } catch (e) { /* */ } } }

  async function takeFiles(list, mode) {
    const files = Array.from(list || []).filter(Boolean);
    if (!files.length || S.busy) return;
    showMsg('');
    const allowMore = MULTI_CATS.indexOf(S.cat) > -1 && MAX_PHOTOS > 1;
    let add = mode === 'add' && allowMore;
    const cap = capFor(S.cat);
    const room = add ? cap - S.photos.length : (allowMore ? cap : 1);
    const pick = files.slice(0, Math.max(1, room));
    const fresh = [];
    let warn = false;
    for (let i = 0; i < pick.length; i += 1) {
      try {
        const p = await readPhoto(pick[i]);
        if (p.small) warn = true;
        fresh.push(p);
      } catch (e) {
        showMsg(t(e.code || 'err_read'));
        if (!fresh.length) return;
      }
    }
    if (!add) { S.photos.forEach(dropPhoto); S.photos = []; }
    S.photos = S.photos.concat(fresh).slice(0, cap);
    if (warn) showMsg(t('err_small'), true);
    renderPhotos();
    setView('photo');
    announce(t('announce_photo'));
    updateCta();
  }

  function renderPhotos() {
    if (!els.photos) return;
    const has = S.photos.length > 0;
    els.photos.hidden = !has || S.view === 'empty';
    if (els.thumbs) {
      els.thumbs.textContent = '';
      const pair = S.cat === 'mixed';
      if (S.photos.length > 1) {
        S.photos.forEach((p, i) => {
          const li = d.createElement('li');
          li.className = 'mn-st-thumb';
          if (pair) {
            const lb = d.createElement('span');
            lb.className = 'mn-st-thumb__l';
            lb.textContent = els.thumbs.getAttribute(i ? 'data-l-pet' : 'data-l-people') || '';
            li.appendChild(lb);
          }
          const img = d.createElement('img');
          img.src = p.url;
          img.alt = `${t('photo_alt')} ${i + 1}`;
          li.appendChild(img);
          const x = d.createElement('button');
          x.type = 'button';
          x.className = 'mn-st-thumb__x';
          x.setAttribute('aria-label', `${t('remove')} ${i + 1}`);
          x.innerHTML = '<span><svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6 6 18"/></svg></span>';
          x.addEventListener('click', () => {
            if (S.busy) return;
            dropPhoto(S.photos[i]);
            S.photos.splice(i, 1);
            renderPhotos();
            if (S.view === 'photo') setView('photo');
          });
          li.appendChild(x);
          els.thumbs.appendChild(li);
        });
      }
    }
    if (els.addWrap) {
      els.addWrap.hidden = !(has && MULTI_CATS.indexOf(S.cat) > -1 && S.photos.length < capFor(S.cat));
      const pet = S.cat === 'mixed' && S.photos.length === 1;
      els.addWrap.classList.toggle('is-pet', pet);
      const at = els.addWrap.querySelector('[data-st-add-t]');
      if (at) at.textContent = at.getAttribute(pet ? 'data-t-pet' : 'data-t-add') || at.textContent;
    }
  }

  if (els.file) els.file.addEventListener('change', () => { takeFiles(els.file.files, 'replace'); els.file.value = ''; });
  if (els.camera) els.camera.addEventListener('change', () => { takeFiles(els.camera.files, 'replace'); els.camera.value = ''; });
  if (els.change) els.change.addEventListener('change', () => { takeFiles(els.change.files, 'replace'); els.change.value = ''; });
  if (els.add) els.add.addEventListener('change', () => { takeFiles(els.add.files, 'add'); els.add.value = ''; });
  if (els.cameraWrap && coarse()) els.cameraWrap.hidden = false;

  // Drag & drop on the stage (and anywhere on the tool, so a near miss still works).
  let dragDepth = 0;
  root.addEventListener('dragenter', (e) => {
    if (!e.dataTransfer || Array.from(e.dataTransfer.types || []).indexOf('Files') < 0) return;
    e.preventDefault();
    dragDepth += 1;
    els.stage.classList.add('is-dragover');
  });
  root.addEventListener('dragover', (e) => { if (dragDepth) { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; } });
  root.addEventListener('dragleave', () => { dragDepth = Math.max(0, dragDepth - 1); if (!dragDepth) els.stage.classList.remove('is-dragover'); });
  root.addEventListener('drop', (e) => {
    if (!dragDepth) return;
    e.preventDefault();
    dragDepth = 0;
    els.stage.classList.remove('is-dragover');
    const more = S.photos.length > 0 && MULTI_CATS.indexOf(S.cat) > -1 && S.photos.length < capFor(S.cat);
    takeFiles(e.dataTransfer.files, more ? 'add' : 'replace');
  });

  /* Stage views ------------------------------------------------------------------------- */
  function setAspect(wd, ht) {
    if (!wd || !ht) return;
    els.stage.style.setProperty('--st-ar', `${wd} / ${ht}`);
    els.stage.style.setProperty('--st-arn', String(Math.round((wd / ht) * 1000) / 1000));
  }
  function setView(view) {
    S.view = view;
    els.stage.setAttribute('data-state', view);
    root.classList.toggle('is-result', view === 'result');
    root.classList.toggle('is-empty', view === 'empty');
    const photo = S.photos[0];
    const res = S.results[S.current];
    if (els.stage.querySelector('[data-st-drop]')) els.stage.querySelector('[data-st-drop]').hidden = view !== 'empty';
    els.photo.hidden = !(view === 'photo' || view === 'busy');
    els.busy.hidden = view !== 'busy';
    els.compare.hidden = view !== 'result';
    if ((view === 'photo' || view === 'busy') && photo) {
      els.photoImg.src = photo.url;
      els.photoImg.alt = t('photo_alt');
      els.photoImg.width = photo.w;
      els.photoImg.height = photo.h;
      setAspect(photo.w, photo.h);
    }
    if (view === 'result' && res) {
      setAspect(res.w, res.h);
      els.compare.classList.toggle('is-solo', !res.beforeUrl);
      if (res.beforeUrl) els.before.src = res.beforeUrl; else els.before.removeAttribute('src');
      els.before.alt = t('photo_alt');
      els.after.src = res.url;
      els.after.alt = t('result_alt', { style: res.name });
      if (els.resultStyle) els.resultStyle.textContent = res.name;
    }
    if (els.result) els.result.hidden = !S.results.length;
    // result buttons only while a result is on the stage; the gallery stays
    const btns = els.result && els.result.querySelector('.mn-st-result__btns');
    if (btns) btns.hidden = view !== 'result';
    if (els.again) els.again.hidden = view !== 'result';
    if (els.reo) { // 23.10: the other orientation of the artwork on the stage (same photo + style, one more preview)
      const r = S.results[S.current];
      const can = LIVE && view === 'result' && !!r && !!S.photos.length && !!r.w && !!r.h;
      els.reo.hidden = !can;
      if (can) els.reo.textContent = els.reo.getAttribute(r.w > r.h ? 'data-l-v' : 'data-l-h') || '';
    }
    const h = els.result && els.result.querySelector('.mn-st-result__h');
    if (h) h.hidden = view !== 'result';
    const lr = els.result && els.result.querySelector('.mn-st-left--result');
    if (lr) lr.hidden = view !== 'result';
    renderPhotos();
    updateCta();
  }

  /* Before / after ------------------------------------------------------------------------ */
  const setPos = (v) => { els.compare.style.setProperty('--st-pos', `${Math.max(0, Math.min(100, v))}%`); };
  if (els.range) {
    els.range.addEventListener('input', () => setPos(parseFloat(els.range.value)));
    setPos(50);
  }
  function revealCompare() {
    if (!els.range) return;
    if (reduce()) { els.range.value = 50; setPos(50); return; }
    els.range.value = 100;
    setPos(100);
    const t0 = performance.now() + 700;
    const tick = (now) => {
      const k = Math.min(1, Math.max(0, (now - t0) / 900));
      const e = 1 - Math.pow(1 - k, 3);
      const v = 100 - 50 * e;
      els.range.value = Math.round(v);
      setPos(v);
      if (k < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  /* Gallery ----------------------------------------------------------------------------- */
  function renderGallery() {
    if (!els.gallery || !els.galleryList) return;
    els.gallery.hidden = S.results.length < (S.restored ? 1 : 2);
    els.galleryList.textContent = '';
    S.results.forEach((r, i) => {
      const li = d.createElement('li');
      const b = d.createElement('button');
      b.type = 'button';
      b.className = 'mn-st-gallery__btn';
      b.setAttribute('aria-label', t('view', { style: r.name }));
      if (i === S.current) b.setAttribute('aria-current', 'true');
      const img = d.createElement('img');
      img.src = r.url;
      img.alt = '';
      b.appendChild(img);
      b.addEventListener('click', () => {
        if (S.busy) return;
        S.current = i;
        renderGallery();
        setView('result');
        revealCompare();
      });
      li.appendChild(b);
      els.galleryList.appendChild(li);
    });
  }

  /* 4. The guided button + counters -------------------------------------------------------- */
  function ctaMode() {
    if (S.busy) return 'busy';
    const res = S.results[S.current];
    if (!S.photos.length && S.view === 'result' && res) return 'puzzle'; // R11: a saved artwork, no photo this visit
    if (!S.photos.length) return 'photo';
    // a result on the stage in the chosen style → the goal: make it a puzzle (pick another style to create again)
    if (S.view === 'result' && res && (!S.style || S.style === res.style)) return 'puzzle';
    if (!S.style) return 'style';
    // no free previews left: the button keeps leading to the goal with the artwork shown / last made
    if (remaining() <= 0) return S.results.length ? 'puzzle' : 'none';
    return 'create';
  }
  function updateCta() {
    const mode = ctaMode();
    const label = mode === 'photo' ? t('photo_h')
      : mode === 'style' ? t('style_h')
        : mode === 'none' ? t('none_btn')
          : mode === 'puzzle' ? t('puzzle')
            : t('create');
    els.creates.forEach((b) => {
      const l = b.querySelector('[data-st-create-label]');
      if (l) l.textContent = label;
      b.setAttribute('data-mode', mode);
      b.classList.toggle('is-busy', mode === 'busy');
      if (mode === 'none' || mode === 'busy') b.setAttribute('aria-disabled', 'true');
      else b.removeAttribute('aria-disabled');
    });
    const n = remaining();
    els.lefts.forEach((p) => { p.textContent = t('left', { n, total: Math.max(LIMIT, n) }); }); // a bonus can lift n above the usual limit
  }

  function scrollToEl(el, block) {
    if (!el) return;
    try { el.scrollIntoView({ behavior: reduce() ? 'auto' : 'smooth', block: block || 'center' }); } catch (e) { el.scrollIntoView(); }
  }

  els.creates.forEach((b) => b.addEventListener('click', () => {
    const mode = ctaMode();
    if (mode === 'busy') return;
    if (mode === 'photo') {
      // a direct click opens the chooser (still inside the user gesture), and the stage comes into view
      scrollToEl(els.stage, 'center');
      if (els.file) els.file.click();
      return;
    }
    if (mode === 'style') {
      showMsg(t('need_style'), true);
      scrollToEl(els.stylesWrap, 'start');
      const first = els.styles[0];
      if (first) setTimeout(() => { try { first.focus({ preventScroll: true }); } catch (e) { /* */ } }, 350);
      return;
    }
    if (mode === 'puzzle') {
      if (S.view !== 'result') { setView('result'); revealCompare(); scrollToEl(els.stage, 'center'); return; }
      toPuzzle().catch((e) => { console.warn('[mn-studio] puzzle', e); puzzleBusy = false; });
      return;
    }
    if (mode === 'none') { showMsg(t('none_left', { total: LIMIT })); openMore('limit'); return; }
    if (!S.ready) { showMsg(t('err_style'), true); return; }
    if (!store.get().email) { openGate(); return; }
    generate();
  }));

  // Hero / FAQ buttons to #studio-app: on phones a tap opens the photo chooser right away (no photo yet).
  d.addEventListener('click', (e) => {
    const a = e.target && e.target.closest ? e.target.closest('[data-st-start]') : null;
    if (!a || S.photos.length || S.busy || !coarse()) return;
    if (els.file) { scrollToEl(els.stage, 'center'); els.file.click(); e.preventDefault(); }
  });

  if (els.reo) els.reo.addEventListener('click', () => {
    const r = S.results[S.current];
    if (S.busy || !r || !S.photos.length) return;
    const b = els.styles.find((x) => x.getAttribute('data-st-style') === r.style);
    if (b) setStyle(b, false);
    S.orient = r.w > r.h ? 'v' : 'h';
    generate().finally(() => { S.orient = ''; });
  });
  if (els.again) els.again.addEventListener('click', () => {
    scrollToEl(els.stylesWrap, 'start');
    const cur = els.styles.find((b) => b.getAttribute('aria-checked') === 'true') || els.styles[0];
    if (cur) setTimeout(() => { try { cur.focus({ preventScroll: true }); } catch (e) { /* */ } }, 350);
  });

  /* 5. E-mail gate ---------------------------------------------------------------------------- */
  let gateOpener = null;
  function openGate() {
    if (!els.gate) { generate(); return; }
    gateOpener = d.activeElement;
    if (els.gateErr) els.gateErr.hidden = true;
    const st = store.get();
    if (els.email && st.email) els.email.value = st.email;
    try { els.gate.showModal(); } catch (e) { els.gate.setAttribute('open', ''); }
    html.classList.add('mn-st-gate-open');
    setTimeout(() => { try { els.email.focus(); } catch (e) { /* */ } }, 60);
    if (LIVE && cfg.turnstile) loadTurnstile();
  }
  function closeGate() {
    if (!els.gate) return;
    try { els.gate.close(); } catch (e) { els.gate.removeAttribute('open'); }
    html.classList.remove('mn-st-gate-open');
    if (gateOpener && gateOpener.focus) { try { gateOpener.focus({ preventScroll: true }); } catch (e) { /* */ } }
  }
  if (els.gateClose) els.gateClose.addEventListener('click', closeGate);
  if (els.gate) {
    els.gate.addEventListener('click', (e) => { if (e.target === els.gate) closeGate(); }); // backdrop
    els.gate.addEventListener('close', () => html.classList.remove('mn-st-gate-open'));
  }
  if (els.gateForm) els.gateForm.addEventListener('submit', (e) => {
    e.preventDefault();
    const email = String((els.email && els.email.value) || '').trim();
    if (!EMAIL_RE.test(email)) {
      if (els.gateErr) { els.gateErr.textContent = t('gate_bad'); els.gateErr.hidden = false; }
      if (els.email) els.email.focus();
      return;
    }
    const consent = cfg.consent === 'implied' ? true : !!(els.consent && els.consent.checked);
    store.set({ email, consent, consentAt: consent ? new Date().toISOString() : null });
    if (consent && LIVE && w.mnOffer && typeof w.mnOffer.subscribe === 'function') {
      // never blocks the artwork: a failed sign-up is only logged
      w.mnOffer.subscribe(email, { source: cfg.source || 'studio' }).catch((err) => console.warn('[mn-studio] subscribe', err && err.message));
    }
    emit('mn-studio:email', { consent, mode: LIVE ? 'live' : 'mock' });
    closeGate();
    generate();
  });

  /* Turnstile (live mode, optional) */
  let tsPromise = null;
  let tsWidget = null;
  let tsResolve = null;
  function loadTurnstile() {
    if (tsPromise) return tsPromise;
    tsPromise = new Promise((resolve, reject) => {
      if (w.turnstile) { resolve(w.turnstile); return; }
      const s = d.createElement('script');
      s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      s.async = true;
      s.onload = () => resolve(w.turnstile);
      s.onerror = () => reject(new Error('turnstile'));
      d.head.appendChild(s);
    });
    return tsPromise;
  }
  function turnstileToken() {
    if (!LIVE || !cfg.turnstile) return Promise.resolve('');
    return loadTurnstile().then((ts) => new Promise((resolve) => {
      tsResolve = resolve;
      if (tsWidget == null) {
        const box = d.createElement('div');
        box.className = 'mn-st-ts';
        els.stage.parentNode.appendChild(box);
        tsWidget = ts.render(box, {
          sitekey: cfg.turnstile,
          appearance: 'interaction-only',
          execution: 'execute',
          callback: (tok) => { if (tsResolve) { tsResolve(tok); tsResolve = null; } },
          'error-callback': () => { if (tsResolve) { tsResolve(''); tsResolve = null; } },
        });
      } else {
        try { ts.reset(tsWidget); } catch (e) { /* */ }
      }
      try { ts.execute(tsWidget); } catch (e) { resolve(''); }
      setTimeout(() => { if (tsResolve) { tsResolve(''); tsResolve = null; } }, 20000);
    })).catch(() => '');
  }

  /* 6. Generation ------------------------------------------------------------------------------ */
  let prog = null;
  function startProgress(expectedMs) {
    stopProgress();
    const steps = ['gen_s1', 'gen_s2', 'gen_s3', 'gen_s4', 'gen_slow'];
    const t0 = performance.now();
    let lastStep = -1;
    const tick = () => {
      const el = performance.now() - t0;
      const k = el / expectedMs;
      // eases to ~90 % at the expected time, creeps towards 96 % after that
      const p = k < 1 ? 0.9 * (1 - Math.pow(1 - k, 2)) : Math.min(0.96, 0.9 + 0.06 * (1 - Math.exp(-(k - 1))));
      if (els.bar) els.bar.parentNode.style.setProperty('--st-p', p.toFixed(3));
      // after ~50 s (live) an honest "taking longer" line instead of the last step
      const slow = LIVE ? el > 50000 : k > 1.6;
      const step = slow ? 4 : k < 0.18 ? 0 : k < 0.5 ? 1 : k < 0.8 ? 2 : 3;
      if (step !== lastStep && els.status) { lastStep = step; els.status.textContent = t(steps[step]); }
      if (els.time) {
        const sec = Math.floor(el / 1000);
        els.time.textContent = `· ${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;
      }
    };
    tick();
    prog = setInterval(tick, 400);
  }
  function stopProgress(done) {
    if (prog) { clearInterval(prog); prog = null; }
    if (done && els.bar) els.bar.parentNode.style.setProperty('--st-p', '1');
    if (!done && els.time) els.time.textContent = '';
  }

  const modelSize = (wd, ht) => {
    const r = wd / ht;
    if (r < 0.85) return { w: 1024, h: 1536 };
    if (r > 1.18) return { w: 1536, h: 1024 };
    return { w: 1024, h: 1024 };
  };

  function fetchT(url, opts, ms) {
    if (!ms || !('AbortController' in w)) return fetch(url, opts);
    const ac = new AbortController();
    const timer = setTimeout(() => ac.abort(), ms);
    return fetch(url, Object.assign({}, opts || {}, { signal: ac.signal })).finally(() => clearTimeout(timer));
  }
  function clientId() {
    let id = store.get().client;
    if (!id) {
      const a = new Uint8Array(12);
      (w.crypto || {}).getRandomValues ? w.crypto.getRandomValues(a) : a.forEach((_, i) => { a[i] = Math.random() * 256; });
      id = Array.from(a, (x) => x.toString(16).padStart(2, '0')).join('');
      store.set({ client: id });
    }
    return id;
  }

  async function mockGenerate(photos, style) {
    const t0 = performance.now();
    const src = photos[0];
    const size = modelSize(src.w, src.h);
    await frame();
    const canvas = paint(src.canvas, style, size.w, size.h);
    const blob = await toBlob(canvas, 'image/jpeg', 0.92);
    const wait = (Math.max(3, parseFloat(cfg.mockDelay) || 8) * 1000) - (performance.now() - t0);
    if (wait > 0) await sleep(wait);
    return { id: `mock-${Date.now().toString(36)}`, blob, w: size.w, h: size.h, printBlob: null, printUrl: '', token: '', resultUrl: '' };
  }

  async function liveGenerate(photos, style, cat) {
    const st = store.get();
    const fd = new FormData();
    photos.forEach((p, i) => fd.append('image', p.blob, `photo-${i + 1}.jpg`));
    fd.append('style', style);
    fd.append('category', cat);
    if (S.orient) fd.append('orientation', S.orient); // 23.10: «vertical / horizontal version» of a result
    // 23.10 (Worker prompts v2): «who» refines the composition (a couple is painted as a couple)
    if (S.who) fd.append('who', S.who);
    fd.append('email', st.email || '');
    fd.append('consent', st.consent ? '1' : '0');
    fd.append('locale', cfg.locale || 'en');
    fd.append('client', clientId());
    const tok = await turnstileToken();
    if (tok) fd.append('turnstile', tok);
    let res;
    try {
      res = await fetchT(`${ENDPOINT}/generate`, { method: 'POST', body: fd, mode: 'cors', credentials: 'omit' }, 180000);
    } catch (e) {
      throw Object.assign(new Error('network'), { code: 'network' });
    }
    let data = {};
    try { data = await res.json(); } catch (e) { data = {}; }
    if (Number.isFinite(data.remaining)) store.set({ remaining: data.remaining });
    if (!res.ok || !data.ok) {
      throw Object.assign(new Error(data.error || `http ${res.status}`), { code: data.error || (res.status === 429 ? 'limit_ip' : 'upstream') });
    }
    // the artwork is painted (and counted) at this point: retry the download a few times before giving up
    let img = null;
    for (let i = 0; i < 3; i += 1) {
      try {
        img = await fetchT(data.result_url, { mode: 'cors', credentials: 'omit' }, 60000);
        if (img.ok) break;
      } catch (e) { img = null; }
      await sleep(1200 * (i + 1));
    }
    if (!img || !img.ok) throw Object.assign(new Error('result'), { code: 'network' });
    const blob = await img.blob();
    return { id: data.id, blob, w: data.width || 1024, h: data.height || 1536, printBlob: null, printUrl: data.print_url || '', token: data.token || '', resultUrl: data.result_url || '' };
  }

  function errorText(err) {
    const c = (err && err.code) || '';
    if (c === 'moderation' || c === 'policy' || c === 'bad_image') return t('err_policy');
    if (c === 'bad_request') return t('err_style'); // R11: the generator does not know this style yet
    if (c === 'limit_email') return t('none_left', { total: LIMIT });
    if (c === 'limit_ip' || c === 'rate') return t('err_limit');
    if (c === 'busy' || c === 'upstream_rate' || c === 'limit_day') return t('err_busy');
    if (c === 'network') return t('err_net');
    return t('err_gen');
  }

  async function generate() {
    if (S.busy || !S.photos.length || !S.style) { updateCta(); return; }
    if (!S.ready) { showMsg(t('err_style'), true); updateCta(); return; }
    if (remaining() <= 0) { showMsg(t('none_left', { total: LIMIT })); updateCta(); openMore('limit'); return; }
    const prevView = S.view;
    S.busy = true;
    root.classList.add('is-busy');
    showMsg('');
    hideNotice();
    pending.set({ at: Date.now(), style: S.style, cat: S.cat, mode: LIVE ? 'live' : 'mock' });
    setView('busy');
    const expected = LIVE ? 35000 : Math.max(3, parseFloat(cfg.mockDelay) || 8) * 1000;
    startProgress(expected);
    if (coarse() || w.innerWidth < 1024) scrollToEl(els.stage, 'center');
    const photos = S.photos.slice();
    const style = S.style;
    const name = S.styleName;
    try {
      const out = LIVE ? await liveGenerate(photos, S.worker || style, S.cat) : await mockGenerate(photos, style);
      if (!LIVE) store.set({ used: (parseInt(store.get().used, 10) || 0) + 1 });
      stopProgress(true);
      await sleep(250);
      S.results.push({
        id: out.id,
        style,
        name,
        blob: out.blob,
        url: URL.createObjectURL(out.blob),
        w: out.w,
        h: out.h,
        beforeUrl: photos[0].url,
        printBlob: out.printBlob,
        printUrl: out.printUrl,
        token: out.token,
        resultUrl: out.resultUrl,
      });
      S.current = S.results.length - 1;
      saveResult(S.results[S.current]);
      S.busy = false;
      root.classList.remove('is-busy');
      renderGallery();
      setView('result');
      revealCompare();
      announce(t('announce_ready'));
      emit('mn-studio:ready', { style, id: out.id, mode: LIVE ? 'live' : 'mock' });
      if (remaining() <= 0) showMsg(t('none_left', { total: LIMIT }), true);
    } catch (err) {
      if (err && err.code === 'limit_email') setTimeout(() => openMore('limit'), 400);
      console.warn('[mn-studio] generate', err && (err.code || err.message));
      stopProgress();
      S.busy = false;
      root.classList.remove('is-busy');
      setView(prevView === 'result' && S.results.length ? 'result' : 'photo');
      showMsg(errorText(err));
    } finally {
      S.busy = false;
      root.classList.remove('is-busy');
      pending.clear();
      updateCta();
    }
  }

  /* 6b. Leaving while painting + the interrupted-preview notice ------------------------------------- */
  w.addEventListener('beforeunload', (e) => {
    if (!S.busy) return undefined;
    e.preventDefault();
    e.returnValue = ''; // older browsers need a value; the browser shows its own "Leave site?" text
    return '';
  });
  function hideNotice() { if (els.notice) els.notice.hidden = true; }
  if (els.noticeX) els.noticeX.addEventListener('click', hideNotice);
  function checkInterrupted() {
    const p = pending.get();
    pending.clear();
    if (!p || !(Date.now() - (p.at || 0) < 30 * 60 * 1000)) { hideNotice(); return; }
    if (!els.notice) return;
    if (els.noticeText) els.noticeText.textContent = t('interrupted');
    els.notice.hidden = false;
    // the style of the interrupted try is chosen again (the category is kept in localStorage anyway)
    const b = els.styles.find((x) => x.getAttribute('data-st-style') === p.style);
    if (b) { S.userStyle = true; setStyle(b, false); }
    const reveal = () => {
      const r = els.notice.getBoundingClientRect();
      if (r.top < 0 || r.bottom > w.innerHeight) {
        try { els.notice.scrollIntoView({ behavior: 'auto', block: 'start' }); } catch (e) { els.notice.scrollIntoView(); }
      }
      try { els.notice.focus({ preventScroll: true }); } catch (e) { /* */ }
    };
    // after the browser's own scroll restoration
    if (d.readyState === 'complete') setTimeout(reveal, 80);
    else w.addEventListener('load', () => setTimeout(reveal, 80), { once: true });
  }

  /* 7. Download (watermarked preview) ------------------------------------------------------------ */
  function blobImage(blob) {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => resolve({ img, done: () => URL.revokeObjectURL(url) });
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('img')); };
      img.src = url;
    });
  }
  async function download() {
    const r = S.results[S.current];
    if (!r) return;
    await ensureBlob(r);
    const { img, done } = await blobImage(r.blob);
    const scale = Math.min(1, 1400 / Math.max(img.naturalWidth, img.naturalHeight));
    const W = Math.round(img.naturalWidth * scale);
    const H = Math.round(img.naturalHeight * scale);
    const c = canvasOf(W, H);
    const x = c.getContext('2d');
    x.drawImage(img, 0, 0, W, H);
    done();
    const mark = 'DAILE STUDIO · PREVIEW';
    const fs = Math.round(W * 0.042);
    x.save();
    x.translate(W / 2, H / 2);
    x.rotate(-Math.PI / 6);
    x.font = `700 ${fs}px "Hanken Grotesk", Arial, sans-serif`;
    x.textAlign = 'center';
    x.textBaseline = 'middle';
    x.lineWidth = Math.max(1, fs / 18);
    const stepY = fs * 3.4;
    const stepX = x.measureText(mark).width + fs * 2;
    const span = Math.hypot(W, H);
    for (let yy = -span / 2, row = 0; yy < span / 2; yy += stepY, row += 1) {
      for (let xx = -span / 2 + (row % 2) * stepX / 2; xx < span / 2; xx += stepX) {
        x.strokeStyle = 'rgba(0, 0, 0, .14)';
        x.strokeText(mark, xx, yy);
        x.fillStyle = 'rgba(255, 255, 255, .34)';
        x.fillText(mark, xx, yy);
      }
    }
    x.restore();
    const band = Math.round(H * 0.055);
    x.fillStyle = 'rgba(11, 11, 11, .62)';
    x.fillRect(0, H - band, W, band);
    x.fillStyle = '#fff';
    x.font = `700 ${Math.round(band * 0.36)}px "Hanken Grotesk", Arial, sans-serif`;
    x.textAlign = 'center';
    x.textBaseline = 'middle';
    x.fillText(`DAILE STUDIO · ${r.name} · daile.art`, W / 2, H - band / 2);
    const blob = await toBlob(c, 'image/jpeg', 0.86);
    const a = d.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `daile-studio-${r.style}-preview.jpg`;
    d.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  }
  // R11: «Download» opens the choice — free preview with a watermark, or the HD file (paid / free with a puzzle)
  if (els.download) els.download.addEventListener('click', () => {
    if (els.hd) { openHd(); return; }
    download().catch((e) => console.warn('[mn-studio] download', e));
  });

  /* 8. Make it a puzzle ----------------------------------------------------------------------------- */
  function idbPut(key, val) {
    return new Promise((resolve, reject) => {
      let rq;
      try { rq = w.indexedDB.open('daile-cz', 1); } catch (e) { reject(e); return; }
      rq.onupgradeneeded = () => { rq.result.createObjectStore('photos'); };
      rq.onerror = () => reject(rq.error);
      rq.onsuccess = () => {
        const db = rq.result;
        const put = (v, onFail) => {
          try {
            const tx = db.transaction('photos', 'readwrite');
            tx.objectStore('photos').put(v, key);
            tx.oncomplete = () => resolve();
            tx.onerror = () => onFail(tx.error);
          } catch (e) { onFail(e); }
        };
        // Blob first; some browsers refuse Blobs in IndexedDB → ArrayBuffer record (the customizer reads both)
        put(val, () => {
          const fr = new FileReader();
          fr.onload = () => put({ buf: fr.result, type: val.type }, reject);
          fr.onerror = () => reject(fr.error);
          fr.readAsArrayBuffer(val);
        });
      };
    });
  }
  async function printBlobOf(r) {
    if (r.printBlob) return r.printBlob;
    await ensureBlob(r);
    if (r.printUrl) {
      try {
        const res = await fetchT(r.printUrl, { mode: 'cors', credentials: 'omit' }, 90000);
        if (res.ok) { r.printBlob = await res.blob(); return r.printBlob; }
      } catch (e) { /* the preview size still works */ }
    }
    return r.blob;
  }
  let puzzleBusy = false;
  async function toPuzzle() {
    const r = S.results[S.current];
    if (!r || puzzleBusy) return;
    puzzleBusy = true;
    if (els.puzzleLabel) els.puzzleLabel.textContent = t('opening');
    els.puzzle.setAttribute('aria-busy', 'true');
    let blob = await printBlobOf(r);
    {
      /* The customizer accepts JPG / PNG only, and judges print quality by pixels: the model's 1024 × 1536
         would show a "low resolution" warning on the 45 × 65 cm puzzle although the print master is made by the
         upscale step (cloud/daile-studio/README.md, "Печать"). Below 3000 px it gets a smooth 2× here. */
      const { img, done } = await blobImage(blob);
      const iw = img.naturalWidth;
      const ih = img.naturalHeight;
      const k = Math.max(iw, ih) < 3000 ? 2 : 1;
      if (k > 1 || (blob.type !== 'image/jpeg' && blob.type !== 'image/png')) {
        const c = k > 1 ? resizeUp(img, iw, ih, iw * k, ih * k) : canvasOf(iw, ih);
        if (k === 1) c.getContext('2d').drawImage(img, 0, 0);
        blob = await toBlob(c, 'image/jpeg', 0.92);
      }
      done();
    }
    // Hand-off record for the production hook (README "Хук для производства"): which STUDIO artwork this is.
    try {
      w.sessionStorage.setItem('daile-studio-handoff', JSON.stringify({
        id: r.id, style: r.style, mode: LIVE ? 'live' : 'mock', result_url: r.resultUrl || '', print_url: r.printUrl || '', w: r.w, h: r.h, at: Date.now(),
      }));
    } catch (e) { /* */ }
    const setQ = cfg.czSet ? `&set=${encodeURIComponent(cfg.czSet)}` : '';
    const url = `${shopRoot}pages/customizer?pdp=1${setQ}`;
    emit('mn-studio:puzzle', { style: r.style, id: r.id, mode: LIVE ? 'live' : 'mock', result_url: r.resultUrl || '', print_url: r.printUrl || '' });
    const reset = () => {
      puzzleBusy = false;
      if (els.puzzleLabel) els.puzzleLabel.textContent = t('puzzle');
      els.puzzle.removeAttribute('aria-busy');
    };
    try {
      await idbPut('photo_pdp-pending', blob);
    } catch (e) {
      console.warn('[mn-studio] hand-off', e);
      reset();
      w.location.href = `${shopRoot}pages/customizer${cfg.czSet ? `?set=${encodeURIComponent(cfg.czSet)}` : ''}`;
      return;
    }
    if (w.mnCzpop && !w.mnCzpop.embedded && typeof w.mnCzpop.open === 'function') {
      w.mnCzpop.open(url);
      setTimeout(reset, 800);
      // the pop-up did not take it (switched off for this tab, etc.) → the separate page
      setTimeout(() => { if (!(w.mnCzpop.isOpen && w.mnCzpop.isOpen())) w.location.href = url; }, 400);
      return;
    }
    w.location.href = url;
  }
  if (els.puzzle) els.puzzle.addEventListener('click', () => { toPuzzle().catch((e) => { console.warn('[mn-studio] puzzle', e); puzzleBusy = false; }); });
  w.addEventListener('pageshow', () => {
    puzzleBusy = false;
    if (els.puzzleLabel) els.puzzleLabel.textContent = t('puzzle');
    if (els.puzzle) els.puzzle.removeAttribute('aria-busy');
  });

  /* ============================================================================================
     R11 (22.10.2026, owner: "what if someone wants more than 3 previews, or to buy the artwork itself"):
     • saved artworks — every finished artwork (id + token + result_url, never the photo) is kept in localStorage
       for 30 days, so a visitor who comes back after the free previews still has their favourite to print / buy;
     • "More previews" dialog (limit reached) and "HD file" dialog (download): order the puzzle (HD file included),
       buy the HD file / a preview pack — or, until those products exist (section settings empty), "Notify me";
     • style filter chips ("Who's in your photo?"), public API window.mnStudio.pick(id) for the page's other sections.
     ============================================================================================ */
  const SAVE_DAYS = 30;
  const SAVE_MAX = 12;
  els.hd = $('[data-st-hd]');
  els.more = $('[data-st-more]');
  els.filters = $$('[data-st-filter]');

  function savedList() {
    const list = Array.isArray(store.get().saved) ? store.get().saved : [];
    const cut = Date.now() - SAVE_DAYS * 864e5;
    return list.filter((x) => x && x.id && x.resultUrl && x.at > cut);
  }
  function saveResult(r) {
    if (!r || !r.resultUrl || !LIVE) return; // mock results are canvas filters, nothing to keep
    const list = savedList().filter((x) => x.id !== r.id);
    list.push({ id: r.id, token: r.token || '', style: r.style, name: r.name, resultUrl: r.resultUrl, printUrl: r.printUrl || '', w: r.w, h: r.h, at: Date.now() });
    store.set({ saved: list.slice(-SAVE_MAX) });
  }
  async function ensureBlob(r) {
    if (r.blob) return r.blob;
    const res = await fetchT(r.resultUrl, { mode: 'cors', credentials: 'omit' }, 60000);
    if (!res.ok) throw Object.assign(new Error('saved'), { code: 'network' });
    r.blob = await res.blob();
    return r.blob;
  }
  function restoreSaved() {
    const list = savedList();
    if (!list.length) return;
    list.forEach((x) => S.results.push({
      id: x.id, style: x.style, name: x.name, blob: null, url: x.resultUrl, w: x.w || 1536, h: x.h || 1024,
      beforeUrl: '', printBlob: null, printUrl: x.printUrl || '', token: x.token || '', resultUrl: x.resultUrl, saved: true,
    }));
    S.restored = true;
    renderGallery();
    if (els.result) els.result.hidden = false;
    root.classList.add('has-saved');
  }

  /* Dialogs (shared helpers) */
  const openDlg = (dlg) => { if (!dlg) return; try { if (!dlg.open) dlg.showModal(); } catch (e) { dlg.setAttribute('open', ''); } };
  const closeDlg = (dlg) => { if (!dlg) return; try { dlg.close(); } catch (e) { dlg.removeAttribute('open'); } };
  [els.hd, els.more].forEach((dlg) => {
    if (!dlg) return;
    dlg.addEventListener('click', (e) => {
      if (e.target === dlg || (e.target.closest && e.target.closest('[data-st-dlg-close]'))) closeDlg(dlg);
    });
  });
  function fillWait(dlg) {
    const em = store.get().email || '';
    $$('[data-st-wait-email]', dlg).forEach((i) => { if (!i.value) i.value = em; });
    $$('[data-st-wait-ok]', dlg).forEach((p) => { p.hidden = true; });
  }
  function openMore(reason) {
    if (!els.more) return;
    els.more.setAttribute('data-reason', reason || 'more');
    const res = S.results[S.current] || S.results[S.results.length - 1];
    const fav = $('[data-st-more-fav]', els.more);
    if (fav) {
      fav.hidden = !res;
      const img = $('img', fav);
      if (img && res) { img.src = res.url; img.alt = t('result_alt', { style: res.name }); }
    }
    fillWait(els.more);
    openDlg(els.more);
  }
  function openHd() {
    const r = S.results[S.current];
    if (!els.hd || !r) return;
    const img = $('[data-st-hd-img]', els.hd);
    if (img) { img.src = r.url; img.alt = t('result_alt', { style: r.name }); }
    const nm = $('[data-st-hd-name]', els.hd);
    if (nm) nm.textContent = r.name;
    fillWait(els.hd);
    openDlg(els.hd);
  }
  d.addEventListener('click', (e) => {
    const el = e.target && e.target.closest ? e.target.closest('[data-st-more-open],[data-st-hd-free],[data-st-hd-puzzle]') : null;
    if (!el) return;
    if (el.hasAttribute('data-st-more-open')) { e.preventDefault(); openMore('plans'); return; }
    if (el.hasAttribute('data-st-hd-free')) { closeDlg(els.hd); download().catch((er) => console.warn('[mn-studio] download', er)); return; }
    if (el.hasAttribute('data-st-hd-puzzle')) {
      closeDlg(els.hd); closeDlg(els.more);
      if (S.results[S.current] || S.results.length) {
        if (!S.results[S.current]) S.current = S.results.length - 1;
        toPuzzle().catch((er) => { console.warn('[mn-studio] puzzle', er); puzzleBusy = false; });
      } else scrollToEl(els.stage, 'center');
    }
  });

  /* Buy (a product chosen in the section settings) or notify-me (no product yet) — page-wide, the plans
     section uses the same buttons. Cart line properties tie the purchase to the artwork for fulfilment. */
  d.addEventListener('click', async (e) => {
    const btn = e.target && e.target.closest ? e.target.closest('[data-st-buy]') : null;
    if (!btn) return;
    e.preventDefault();
    const kind = btn.getAttribute('data-st-buy') || 'hd';
    const variant = btn.getAttribute('data-variant') || '';
    const box = btn.closest('[data-st-offer]') || btn.parentNode;
    if (!variant) {
      const form = box && box.querySelector('[data-st-wait]');
      if (form) { form.hidden = false; const i = form.querySelector('input'); if (i) { if (!i.value) i.value = store.get().email || ''; i.focus(); } }
      return;
    }
    const r = S.results[S.current];
    const props = { _studio_kind: kind };
    if (kind === 'hd' && r) {
      Object.assign(props, { Artwork: `DAILE STUDIO · ${r.name}`, _studio_id: r.id, _studio_token: r.token || '', _studio_result: r.resultUrl || '' });
    }
    const label = btn.textContent;
    btn.setAttribute('aria-busy', 'true');
    btn.textContent = t('busy_more');
    try {
      const item = { id: Number(variant), quantity: 1, properties: props };
      const plan = btn.getAttribute('data-selling-plan'); // 25.10: subscription products (Studio Club) need their selling plan
      if (plan) item.selling_plan = Number(plan);
      const res = await fetch(`${shopRoot}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [item] }) });
      if (!res.ok) throw new Error(`cart ${res.status}`);
      emit('mn-studio:buy', { kind, variant });
      w.location.href = `${shopRoot}cart`;
    } catch (er) {
      console.warn('[mn-studio] buy', er);
      btn.textContent = label;
      btn.removeAttribute('aria-busy');
    }
  });
  d.addEventListener('submit', (e) => {
    const form = e.target && e.target.closest ? e.target.closest('[data-st-wait]') : null;
    if (!form) return;
    e.preventDefault();
    const input = form.querySelector('input[type="email"]');
    const ok = form.parentNode.querySelector('[data-st-wait-ok]') || form.querySelector('[data-st-wait-ok]');
    const err = form.querySelector('[data-st-wait-err]');
    const email = (input && input.value || '').trim();
    if (!EMAIL_RE.test(email)) { if (err) { err.textContent = t('wait_bad'); err.hidden = false; } return; }
    if (err) err.hidden = true;
    const kind = form.getAttribute('data-st-wait') || 'studio';
    if (!store.get().email) store.set({ email });
    const done = () => { form.hidden = true; if (ok) { ok.textContent = t('wait_ok'); ok.hidden = false; } emit('mn-studio:waitlist', { kind }); };
    if (w.mnOffer && typeof w.mnOffer.subscribe === 'function' && LIVE) {
      Promise.resolve(w.mnOffer.subscribe(email, { source: `studio-${kind}` })).catch((er) => console.warn('[mn-studio] waitlist', er)).then(done);
    } else done();
  });

  /* Style filter chips: "Who's in your photo?" */
  function setFilter(f, fromCat) {
    if (!els.filters.length) return;
    els.filters.forEach((b) => b.setAttribute('aria-pressed', b.getAttribute('data-st-filter') === f ? 'true' : 'false'));
    els.styles.forEach((b) => {
      const best = ` ${(b.getAttribute('data-st-best') || '').replace(/,/g, ' ')} `;
      const show = f === 'all' || best.indexOf(` ${f} `) > -1 || b.getAttribute('aria-checked') === 'true';
      b.hidden = !show;
    });
    if (els.stylesWrap) els.stylesWrap.setAttribute('data-filter', f);
    if (!fromCat) store.set({ filter: f });
  }
  els.filters.forEach((b) => b.addEventListener('click', () => setFilter(b.getAttribute('data-st-filter'), false)));
  function pickStyle(id) {
    const b = els.styles.find((x) => x.getAttribute('data-st-style') === id);
    if (!b) return false;
    if (b.hidden) b.hidden = false;
    S.userStyle = true;
    setStyle(b, false);
    return true;
  }
  // Links / buttons elsewhere on the page: [data-st-pick="<style id>"] → choose it in the tool and go there
  d.addEventListener('click', (e) => {
    const a = e.target && e.target.closest ? e.target.closest('[data-st-pick]') : null;
    if (!a) return;
    if (pickStyle(a.getAttribute('data-st-pick'))) {
      e.preventDefault();
      scrollToEl(S.photos.length ? els.stylesWrap : els.stage, 'center');
    }
  });

  /* ============================================================================================
     Mock painters — canvas stand-ins for the styles, so the flow can be tried before the backend
     is live. Deliberately simple and fast (≈ 0.2–0.6 s at 1024 × 1536). Not the real look.
     ============================================================================================ */
  const rand = (() => { let s = 1234567; return () => { s = (s * 16807) % 2147483647; return (s - 1) / 2147483646; }; })();
  const hex = (h) => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
  const lerp = (a, b, k) => a + (b - a) * k;
  const clamp255 = (v) => (v < 0 ? 0 : v > 255 ? 255 : v);

  function cover(src, W, H) {
    const c = canvasOf(W, H);
    const x = c.getContext('2d');
    const s = Math.max(W / src.width, H / src.height);
    const dw = src.width * s;
    const dh = src.height * s;
    x.imageSmoothingQuality = 'high';
    x.drawImage(src, (W - dw) / 2, (H - dh) / 2, dw, dh);
    return c;
  }
  function adjust(c, o) {
    const x = c.getContext('2d');
    const im = x.getImageData(0, 0, c.width, c.height);
    const p = im.data;
    const sat = o.sat == null ? 1 : o.sat;
    const con = o.con == null ? 1 : o.con;
    const bri = o.bri || 0;
    const warm = o.warm || 0;
    for (let i = 0; i < p.length; i += 4) {
      let r = p[i]; let g = p[i + 1]; let b = p[i + 2];
      const l = 0.299 * r + 0.587 * g + 0.114 * b;
      r = l + (r - l) * sat; g = l + (g - l) * sat; b = l + (b - l) * sat;
      r = (r - 128) * con + 128 + bri + warm;
      g = (g - 128) * con + 128 + bri + warm * 0.35;
      b = (b - 128) * con + 128 + bri - warm;
      p[i] = clamp255(r); p[i + 1] = clamp255(g); p[i + 2] = clamp255(b);
    }
    x.putImageData(im, 0, 0);
    return c;
  }
  function blurred(c, f) {
    const sm = resizeTo(c, c.width, c.height, Math.max(1, Math.round(c.width / f)), Math.max(1, Math.round(c.height / f)));
    const out = canvasOf(c.width, c.height);
    const x = out.getContext('2d');
    x.imageSmoothingQuality = 'high';
    x.drawImage(sm, 0, 0, out.width, out.height);
    return out;
  }
  function lumaOf(c) {
    const im = c.getContext('2d').getImageData(0, 0, c.width, c.height).data;
    const L = new Float32Array(c.width * c.height);
    for (let i = 0, j = 0; j < L.length; i += 4, j += 1) L[j] = (0.299 * im[i] + 0.587 * im[i + 1] + 0.114 * im[i + 2]) / 255;
    return L;
  }
  function edges(c, f) {
    // Sobel on a 1/f copy → canvas of black lines (alpha = strength)
    const sm = resizeTo(c, c.width, c.height, Math.round(c.width / f), Math.round(c.height / f));
    const W = sm.width; const H = sm.height;
    const L = lumaOf(sm);
    const out = canvasOf(W, H);
    const ox = out.getContext('2d');
    const im = ox.createImageData(W, H);
    const q = im.data;
    for (let y = 1; y < H - 1; y += 1) {
      for (let x = 1; x < W - 1; x += 1) {
        const i = y * W + x;
        const gx = -L[i - W - 1] - 2 * L[i - 1] - L[i + W - 1] + L[i - W + 1] + 2 * L[i + 1] + L[i + W + 1];
        const gy = -L[i - W - 1] - 2 * L[i - W] - L[i - W + 1] + L[i + W - 1] + 2 * L[i + W] + L[i + W + 1];
        const m = Math.min(1, Math.hypot(gx, gy) * 1.6);
        const a = m > 0.28 ? Math.min(255, (m - 0.28) * 520) : 0;
        q[i * 4 + 3] = a;
      }
    }
    ox.putImageData(im, 0, 0);
    return out;
  }
  function texture(x, W, H, alpha, size) {
    const n = canvasOf(size || 128, size || 128);
    const nx = n.getContext('2d');
    const im = nx.createImageData(n.width, n.height);
    for (let i = 0; i < im.data.length; i += 4) {
      const v = 110 + rand() * 120;
      im.data[i] = v; im.data[i + 1] = v; im.data[i + 2] = v; im.data[i + 3] = 255;
    }
    nx.putImageData(im, 0, 0);
    x.save();
    x.globalAlpha = alpha;
    x.globalCompositeOperation = 'overlay';
    x.fillStyle = x.createPattern(n, 'repeat');
    x.fillRect(0, 0, W, H);
    x.restore();
  }
  function vignette(x, W, H, color, strength) {
    const g = x.createRadialGradient(W / 2, H * 0.45, Math.min(W, H) * 0.25, W / 2, H / 2, Math.hypot(W, H) * 0.62);
    g.addColorStop(0, 'rgba(0,0,0,0)');
    g.addColorStop(1, color);
    x.save();
    x.globalAlpha = strength;
    x.globalCompositeOperation = 'multiply';
    x.fillStyle = g;
    x.fillRect(0, 0, W, H);
    x.restore();
  }
  // Brush strokes sampled from `src` (ImageData), oriented by angleFn(x, y, L).
  function strokes(x, src, W, H, o) {
    const p = src.data;
    const L = o.L;
    const count = Math.round((W * H) / o.density);
    x.save();
    x.lineCap = 'round';
    x.globalAlpha = o.alpha;
    for (let k = 0; k < count; k += 1) {
      const px = rand() * W;
      const py = rand() * H;
      const ix = Math.min(W - 1, px | 0);
      const iy = Math.min(H - 1, py | 0);
      const i = (iy * W + ix) * 4;
      let r = p[i]; let g = p[i + 1]; let b = p[i + 2];
      if (o.tint) { const c = o.tint(r, g, b); r = c[0]; g = c[1]; b = c[2]; }
      const len = lerp(o.len[0], o.len[1], rand());
      const wid = lerp(o.wid[0], o.wid[1], rand());
      let a;
      if (o.angle === 'edge' && L) {
        const xm = Math.max(1, Math.min(W - 2, ix)); const ym = Math.max(1, Math.min(H - 2, iy));
        const gx = L[ym * W + xm + 1] - L[ym * W + xm - 1];
        const gy = L[(ym + 1) * W + xm] - L[(ym - 1) * W + xm];
        a = Math.abs(gx) + Math.abs(gy) > 0.02 ? Math.atan2(gy, gx) + Math.PI / 2 : (o.base || 0) + (rand() - 0.5) * 1.2;
      } else if (typeof o.angle === 'function') {
        a = o.angle(px, py);
      } else {
        a = (o.base || 0) + (rand() - 0.5) * (o.spread == null ? 1 : o.spread);
      }
      const dx = Math.cos(a) * len / 2;
      const dy = Math.sin(a) * len / 2;
      x.strokeStyle = `rgb(${r | 0},${g | 0},${b | 0})`;
      x.lineWidth = wid;
      x.beginPath();
      x.moveTo(px - dx, py - dy);
      x.lineTo(px + dx, py + dy);
      x.stroke();
    }
    x.restore();
  }
  function overlayImage(x, c, alpha, mode) {
    x.save();
    x.globalAlpha = alpha;
    if (mode) x.globalCompositeOperation = mode;
    x.drawImage(c, 0, 0);
    x.restore();
  }
  function mapPixels(c, fn) {
    const cx = c.getContext('2d');
    const im = cx.getImageData(0, 0, c.width, c.height);
    const p = im.data;
    for (let i = 0; i < p.length; i += 4) {
      const out = fn(p[i], p[i + 1], p[i + 2], (i / 4) % c.width, ((i / 4) / c.width) | 0);
      p[i] = out[0]; p[i + 1] = out[1]; p[i + 2] = out[2];
    }
    cx.putImageData(im, 0, 0);
    return c;
  }
  function hsl(r, g, b) {
    r /= 255; g /= 255; b /= 255;
    const mx = Math.max(r, g, b); const mn = Math.min(r, g, b);
    const l = (mx + mn) / 2;
    if (mx === mn) return [0, 0, l];
    const dd = mx - mn;
    const s = l > 0.5 ? dd / (2 - mx - mn) : dd / (mx + mn);
    let h;
    if (mx === r) h = (g - b) / dd + (g < b ? 6 : 0);
    else if (mx === g) h = (b - r) / dd + 2;
    else h = (r - g) / dd + 4;
    return [h * 60, s, l];
  }

  const PAINTERS = {
    oil(base, W, H) {
      adjust(base, { sat: 1.08, con: 1.18, warm: 14, bri: -8 });
      const out = blurred(base, 7);
      const x = out.getContext('2d');
      const src = base.getContext('2d').getImageData(0, 0, W, H);
      strokes(x, src, W, H, { density: 60, len: [9, 18], wid: [3, 6.5], alpha: 0.72, angle: 'edge', L: lumaOf(base), base: -0.6 });
      overlayImage(x, base, 0.34);
      vignette(x, W, H, 'rgb(40, 20, 6)', 0.85);
      texture(x, W, H, 0.12);
      return out;
    },
    impressionist(base, W, H) {
      adjust(base, { sat: 1.32, con: 0.92, bri: 16 });
      const out = blurred(base, 9);
      const x = out.getContext('2d');
      const src = base.getContext('2d').getImageData(0, 0, W, H);
      const pal = ['#f4e3a1', '#c9b5e8', '#a5d68e', '#a9cbe8', '#f6c1cf'].map(hex);
      strokes(x, src, W, H, {
        density: 34, len: [6, 12], wid: [4, 7.5], alpha: 0.88, base: -0.5, spread: 1.1,
        tint: (r, g, b) => { const c = pal[(rand() * pal.length) | 0]; const k = 0.18; return [lerp(r, c[0], k) + 8, lerp(g, c[1], k) + 8, lerp(b, c[2], k) + 8]; },
      });
      overlayImage(x, base, 0.16);
      texture(x, W, H, 0.08);
      return out;
    },
    starry(base, W, H) {
      adjust(base, { sat: 1.5, con: 1.22 });
      const deep = hex('#13265f'); const cob = hex('#2f5fb3'); const sky = hex('#7fb0d9'); const gold = hex('#f2c230');
      mapPixels(base, (r, g, b) => {
        const l = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        const c = l < 0.3 ? deep : l < 0.55 ? cob : l < 0.78 ? sky : gold;
        const k = 0.42;
        return [lerp(r, c[0], k), lerp(g, c[1], k), lerp(b, c[2], k)];
      });
      const out = blurred(base, 8);
      const x = out.getContext('2d');
      const src = base.getContext('2d').getImageData(0, 0, W, H);
      const flow = (px, py) => Math.sin(px / 85) * 1.6 + Math.cos(py / 70) * 1.6 + Math.sin((px + py) / 160);
      strokes(x, src, W, H, { density: 44, len: [14, 26], wid: [3, 5.5], alpha: 0.9, angle: flow });
      overlayImage(x, base, 0.14);
      texture(x, W, H, 0.1);
      return out;
    },
    gilded(base, W, H) {
      adjust(base, { sat: 1.18, con: 1.16, warm: 6 });
      const dark = hex('#5a3b0b'); const light = hex('#f3d27a');
      const cxm = W / 2; const cym = H * 0.42; const rmax = Math.hypot(W / 2, H / 2);
      mapPixels(base, (r, g, b, px, py) => {
        const l = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        const rad = Math.hypot(px - cxm, py - cym) / rmax;
        const k = Math.max(0, Math.min(0.78, (rad - 0.18) * 1.5));
        return [lerp(r, lerp(dark[0], light[0], l), k), lerp(g, lerp(dark[1], light[1], l), k), lerp(b, lerp(dark[2], light[2], l), k)];
      });
      const out = canvasOf(W, H);
      const x = out.getContext('2d');
      x.drawImage(base, 0, 0);
      const cell = 16;
      const jewels = ['#2f7d5b', '#8f2436', '#23466e', '#e8c45c', '#f6e3a0', '#b8862b'];
      for (let y = 0; y < H; y += cell) {
        for (let xx = 0; xx < W; xx += cell) {
          const rad = Math.hypot(xx - cxm, y - cym) / rmax;
          if (rad < 0.34 || rand() > 0.62) continue;
          x.globalAlpha = 0.5 + rand() * 0.3;
          x.fillStyle = jewels[(rand() * jewels.length) | 0];
          if (rand() < 0.35) {
            x.beginPath(); x.arc(xx + cell / 2, y + cell / 2, cell * 0.36, 0, Math.PI * 2); x.fill();
          } else {
            x.fillRect(xx + 2, y + 2, cell - 4, cell - 4);
          }
        }
      }
      x.globalAlpha = 0.55;
      x.strokeStyle = '#f3d27a';
      x.lineWidth = 2;
      for (let k = 0; k < 70; k += 1) {
        const px = rand() * W; const py = rand() * H;
        if (Math.hypot(px - cxm, py - cym) / rmax < 0.4) continue;
        x.beginPath();
        for (let a = 0, rr = 2; a < Math.PI * 5; a += 0.3, rr += 0.9) x.lineTo(px + Math.cos(a) * rr, py + Math.sin(a) * rr);
        x.stroke();
      }
      x.globalAlpha = 1;
      const sheen = x.createLinearGradient(0, 0, W, H);
      sheen.addColorStop(0, 'rgba(255, 236, 170, .18)');
      sheen.addColorStop(0.5, 'rgba(255, 255, 255, 0)');
      sheen.addColorStop(1, 'rgba(255, 220, 140, .16)');
      x.globalCompositeOperation = 'overlay';
      x.fillStyle = sheen;
      x.fillRect(0, 0, W, H);
      x.globalCompositeOperation = 'source-over';
      texture(x, W, H, 0.06);
      return out;
    },
    seaside(base, W, H) {
      adjust(base, { sat: 1.16, con: 1.04, bri: 20 });
      mapPixels(base, (r, g, b) => {
        const l = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
        const sh = 1 - l;
        return [r + 12 * l - 4 * sh, g + 8 * l - 6 * sh, b - 8 * l + 16 * sh];
      });
      const out = canvasOf(W, H);
      const x = out.getContext('2d');
      x.drawImage(base, 0, 0);
      const bloom = blurred(base, 14);
      overlayImage(x, bloom, 0.38, 'screen');
      const src = base.getContext('2d').getImageData(0, 0, W, H);
      strokes(x, src, W, H, { density: 80, len: [8, 15], wid: [3, 5], alpha: 0.5, base: 0, spread: 0.7 });
      overlayImage(x, base, 0.22);
      texture(x, W, H, 0.06);
      return out;
    },
    preraphaelite(base, W, H) {
      // jewel tones: emerald shadows, rose / terracotta warmth, teal greens, a soft glow and fine strokes
      adjust(base, { sat: 1.14, con: 1.1, warm: 10, bri: 4 });
      const em = hex('#1f4f43'); const teal = hex('#2f6f73'); const rose = hex('#c8706c'); const terra = hex('#b5643c'); const cream = hex('#f3e2c4');
      mapPixels(base, (r, g, b) => {
        const [h, , l] = hsl(r, g, b);
        let c = null; let k = 0;
        if (l < 0.32) { c = em; k = 0.3; } else if (l > 0.84) { c = cream; k = 0.22; } else if (h >= 330 || h < 28) { c = rose; k = 0.18; } else if (h < 60) { c = terra; k = 0.14; } else if (h < 210) { c = teal; k = 0.24; }
        return c ? [lerp(r, c[0], k), lerp(g, c[1], k), lerp(b, c[2], k)] : [r, g, b];
      });
      const out = blurred(base, 5);
      const x = out.getContext('2d');
      const src = base.getContext('2d').getImageData(0, 0, W, H);
      strokes(x, src, W, H, { density: 70, len: [6, 12], wid: [2, 3.6], alpha: 0.62, angle: 'edge', L: lumaOf(base), base: -0.3 });
      overlayImage(x, base, 0.48);
      overlayImage(x, blurred(base, 14), 0.24, 'soft-light');
      vignette(x, W, H, 'rgb(18, 40, 32)', 0.6);
      texture(x, W, H, 0.08);
      return out;
    },
    woodblock(base, W, H) {
      const f = 2;
      const sm = resizeTo(base, W, H, Math.round(W / f), Math.round(H / f));
      adjust(sm, { sat: 1.2, con: 1.15 });
      const P = {
        ink: hex('#1b1b22'), indigo: hex('#22406b'), blue: hex('#3a6a9a'), sky: hex('#9fb8cf'),
        dred: hex('#7a2a20'), red: hex('#c4472f'), salmon: hex('#e0907a'), blush: hex('#efcfb8'),
        ochre: hex('#d9a441'), cream: hex('#f1e4c8'), beige: hex('#d8c7a3'), slate: hex('#5b6f86'),
        sage: hex('#5f7a52'), sageL: hex('#a9b98f'),
      };
      mapPixels(sm, (r, g, b) => {
        const [h, s, l] = hsl(r, g, b);
        if (l < 0.16) return P.ink;
        if (s < 0.2) return l > 0.78 ? P.cream : l > 0.55 ? P.beige : l > 0.35 ? P.slate : P.indigo;
        if (h < 40 || h >= 330) return l > 0.78 ? P.blush : l > 0.58 ? P.salmon : l > 0.36 ? P.red : P.dred;
        if (h < 70) return l > 0.62 ? P.cream : P.ochre;
        if (h < 170) return l > 0.55 ? P.sageL : P.sage;
        return l > 0.66 ? P.sky : l > 0.4 ? P.blue : P.indigo;
      });
      const out = canvasOf(W, H);
      const x = out.getContext('2d');
      x.imageSmoothingEnabled = false;
      x.drawImage(sm, 0, 0, W, H);
      x.imageSmoothingEnabled = true;
      const ed = edges(base, 2);
      x.drawImage(ed, 0, 0, W, H);
      x.drawImage(ed, 0, 0, W, H);
      texture(x, W, H, 0.14, 96);
      return out;
    },
    expressionist(base, W, H) {
      // Blaue Reiter colour: unmixed greens, pinks, yellows, blue / violet shadows, broad flat strokes, dark contours
      const f = 2;
      const sm = resizeTo(base, W, H, Math.round(W / f), Math.round(H / f));
      adjust(sm, { sat: 1.55, con: 1.12, bri: 6 });
      const P = {
        ink: hex('#1d1a3a'), violet: hex('#5b3f8f'), blue: hex('#2d5fb0'), sky: hex('#7fb6e6'),
        green: hex('#1f9a4a'), lime: hex('#9bd04a'), yellow: hex('#f4d23c'), orange: hex('#f08a2c'),
        red: hex('#d8342c'), pink: hex('#f07fa8'), blush: hex('#f6c3b0'), cream: hex('#fbf0d6'),
      };
      mapPixels(sm, (r, g, b) => {
        const [h, s2, l] = hsl(r, g, b);
        if (l < 0.17) return P.ink;
        if (l < 0.3) return h > 180 && h < 300 ? P.violet : P.blue;
        if (s2 < 0.16) return l > 0.8 ? P.cream : l > 0.58 ? P.blush : P.sky;
        if (h < 18 || h >= 340) return l > 0.66 ? P.pink : P.red;
        if (h < 42) return l > 0.7 ? P.blush : P.orange;
        if (h < 70) return P.yellow;
        if (h < 165) return l > 0.55 ? P.lime : P.green;
        if (h < 250) return l > 0.6 ? P.sky : P.blue;
        return l > 0.6 ? P.pink : P.violet;
      });
      const flat = canvasOf(W, H);
      const fx = flat.getContext('2d');
      fx.imageSmoothingQuality = 'high';
      fx.drawImage(sm, 0, 0, W, H);
      const out = blurred(flat, 3);
      const x = out.getContext('2d');
      const src = flat.getContext('2d').getImageData(0, 0, W, H);
      strokes(x, src, W, H, { density: 46, len: [12, 22], wid: [5, 9], alpha: 0.85, angle: 'edge', L: lumaOf(base), base: 0.4 });
      const ed = edges(base, 2);
      x.save();
      x.globalAlpha = 0.55;
      x.drawImage(ed, 0, 0, W, H);
      x.restore();
      texture(x, W, H, 0.1);
      return out;
    },
  };

  function paint(src, style, W, H) {
    const base = cover(src, W, H);
    const fn = PAINTERS[style] || PAINTERS.oil;
    try { return fn(base, W, H); } catch (e) { console.warn('[mn-studio] mock painter', e); return base; }
  }

  /* Start ------------------------------------------------------------------------------------------ */
  const preStyle = els.styles.find((b) => b.getAttribute('aria-checked') === 'true');
  setCat(S.who, false);
  if (preStyle) { S.userStyle = true; setStyle(preStyle, false); }
  setView('empty');
  restoreSaved();
  checkInterrupted();
  syncExStyle();
  updateCta();
  started = true;
  root.classList.add('is-ready');

  w.mnStudio = Object.freeze({
    mode: LIVE ? 'live' : 'mock',
    remaining,
    pick: pickStyle,
    more: () => openMore('plans'),
    hd: openHd,
    reset: () => { store.clear(); updateCta(); },
    // dev helper: the mock painter for a style on the current photo → canvas
    mockPaint: (style) => (S.photos[0] ? paint(S.photos[0].canvas, style, modelSize(S.photos[0].w, S.photos[0].h).w, modelSize(S.photos[0].w, S.photos[0].h).h) : null),
  });
})();
